import React from 'react'
import { useGlobal } from './GlobalContext'
import {
  desfazSolicitacaoCancelamento,
  getImagensRetencao,
  obtemMotivosCancelamento,
  solicitaCancelamento,
  suspendeAssinatura,
  getNumeroKitsMulta
} from '../api/cancelamento.servico'
import { isEmptyObject } from '../utils/helpers/helpers'
import { httpStatus } from '../utils/request/httpStatus'

const SolicitacaoCancelamentoContext = React.createContext()

function SolicitacaoCancelamentoProvider( props ) {
  const [ state, setState ] = React.useState( {
    'aguardandoCancelamento': false,
    'aguardandoSuspensao': false,
    'assinaturaCancelada': false,
    'assinaturaSuspensao': false,
    'confirmacaoDesfazerCancelamento': false,
    'dadosKitsCobrancas': null,
    'imagensRetencao': null,
    'motivosCancelamento': null,
    'numeroKitsMulta': 0,
    'solicitacaoCancelamentoDesfeita': false
  } )

  const value = React.useMemo( () => {
    return {
      setState,
      state
    }
  }, [ state ] )

  return (
    <SolicitacaoCancelamentoContext.Provider
      value={ value }
      { ...props }
    />
  )
}

function useSolicitacaoCancelamento() {
  const context = React.useContext( SolicitacaoCancelamentoContext )
  if ( !context ) {
    throw new Error( 'useSolicitacaoCancelamento deve ser usado dentro de um Provider' )
  }

  const { state, setState } = context
  const { setErroRequisicao } = useGlobal()

  const setAssinaturaCancelada = assinaturaCancelada => {
    setState( prevState => ( {
      ...prevState,
      assinaturaCancelada
    } ) )
  }

  const setMotivosCancelamento = motivosCancelamento => {
    setState( prevState => ( {
      ...prevState,
      motivosCancelamento
    } ) )
  }

  const setAssinaturaSuspensao = assinaturaSuspensao => {
    setState( prevState => ( {
      ...prevState,
      assinaturaSuspensao
    } ) )
  }

  const setDadosKitsCobrancas = dadosKitsCobrancas => {
    setState( prevState => ( {
      ...prevState,
      dadosKitsCobrancas
    } ) )
  }

  const setSolicitacaoCancelamentoDesfeita = solicitacaoCancelamentoDesfeita => {
    setState( prevState => ( {
      ...prevState,
      solicitacaoCancelamentoDesfeita
    } ) )
  }

  const setConfirmacaoDesfazerCancelamento = confirmacaoDesfazerCancelamento => {
    setState( prevState => ( {
      ...prevState,
      confirmacaoDesfazerCancelamento
    } ) )
  }

  const setImagensRetencao = imagensRetencao => {
    setState( prevState => ( {
      ...prevState,
      imagensRetencao
    } ) )
  }

  const setAguardandoSuspensao = aguardandoSuspensao => {
    setState( prevState => ( {
      ...prevState,
      aguardandoSuspensao
    } ) )
  }

  const setAguardandoCancelamento = aguardandoCancelamento => {
    setState( prevState => ( {
      ...prevState,
      aguardandoCancelamento
    } ) )
  }

  const setNumeroKitsMulta = numeroKitsMulta => {
    setState( prevState => ( {
      ...prevState,
      numeroKitsMulta
    } ) )
  }

  const obterMotivosCancelamento = async() => {
    try {
      const response = await obtemMotivosCancelamento()
      const motivos = {
        'DEFAULT': {
          'codigo': 'DEFAULT',
          'texto': 'Motivo do cancelamento'
        },
        ...response.data.cancelamento.curadoria.motivos
      }
      setMotivosCancelamento( motivos )
    } catch ( erro ) {
      setErroRequisicao( erro.response.status )
    }
  }

  const cancelarAssinatura = async( motivos, idAssinatura ) => {
    setAguardandoCancelamento( true )
    try {
      await solicitaCancelamento( idAssinatura, motivos )
      setAssinaturaCancelada( true )
    } catch ( erro ) {
      setAguardandoCancelamento( false )
      setErroRequisicao( erro.response.status )
    }
  }

  const suspenderAssinatura = async( motivos, idAssinatura ) => {
    setAguardandoSuspensao( true )
    try {
      const response = await suspendeAssinatura( idAssinatura, motivos )
      setAssinaturaSuspensao( true )
      if ( response.data ) {
        setDadosKitsCobrancas( response.data )
      }
    } catch ( erro ) {
      setAguardandoSuspensao( false )
      setErroRequisicao( erro.response.status )
    }
  }

  const ofereceDesfazerCancelamento = () => {
    setConfirmacaoDesfazerCancelamento( true )
  }

  const desfazerCancelamento = async idAssinatura => {
    try {
      await desfazSolicitacaoCancelamento( idAssinatura )
      setSolicitacaoCancelamentoDesfeita( true )
    } catch ( erro ) {
      setErroRequisicao( erro.response.status )
    }
  }

  const pegarImagensRetencao = async idAssinatura => {
    try {
      const response = await getImagensRetencao( idAssinatura )
      if ( !isEmptyObject( response.data.imagens ) && response.status === httpStatus.OK ) {
        setImagensRetencao( response.data.imagens )
      }
    } catch ( erro ) {
      setErroRequisicao( erro.response.status )
    }
  }

  const getQuantidadeKitsMulta = async idAssinatura => {
    try {
      const response = await getNumeroKitsMulta( idAssinatura )
      if ( response.data.numeroDeKitsMulta >= 0 && response.status === httpStatus.OK ) {
        setNumeroKitsMulta( response.data.numeroDeKitsMulta )
      }
    } catch ( erro ) {
      setErroRequisicao( erro.response.status )
    }
  }

  return {
    'aguardandoCancelamento': state.aguardandoCancelamento,
    'aguardandoSuspensao': state.aguardandoSuspensao,
    'assinaturaCancelada': state.assinaturaCancelada,
    'assinaturaSuspensao': state.assinaturaSuspensao,
    cancelarAssinatura,
    'confirmacaoDesfazerCancelamento': state.confirmacaoDesfazerCancelamento,
    'dadosKitsCobrancas': state.dadosKitsCobrancas,
    desfazerCancelamento,
    getQuantidadeKitsMulta,
    'imagensRetencao': state.imagensRetencao,
    'motivosCancelamento': state.motivosCancelamento,
    'numeroKitsMulta': state.numeroKitsMulta,
    obterMotivosCancelamento,
    ofereceDesfazerCancelamento,
    pegarImagensRetencao,
    setConfirmacaoDesfazerCancelamento,
    setSolicitacaoCancelamentoDesfeita,
    'solicitacaoCancelamentoDesfeita': state.solicitacaoCancelamentoDesfeita,
    suspenderAssinatura,
  }
}

export { SolicitacaoCancelamentoProvider, useSolicitacaoCancelamento }
